import React, { useState, Children, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Moon, Check, X, Clock, BookOpen, Shield, Star, ArrowRight } from 'lucide-react';
// --- Theme Constants ---
const COLORS = {
  charcoal: '#1a1c23',
  cream: '#fdfbf7',
  teal: '#5a8f8f',
  amber: '#d4a574',
  amberHover: '#c29463',
  textDark: '#e2e8f0',
  textLight: '#334155'
};
// --- Animation Variants ---
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
const itemVariant = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
// --- Modal Component ---
const Modal = ({
  isOpen,
  onClose,
  title,
  children
}: any) => {
  return <AnimatePresence data-id="element-2">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" data-id="element-3" />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none" data-id="element-4">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden pointer-events-auto" data-id="element-5">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-stone-200" data-id="element-6">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-7">{title}</h2>
                <button onClick={onClose} className="p-2 hover:bg-stone-100 rounded-full transition-colors" data-id="element-8">
                  <X className="w-5 h-5 text-slate-600" data-id="element-9" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]" data-id="element-10">
                {children}
              </div>
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
};
// --- Components ---
const Button = ({
  children,
  className = '',
  fullWidth = false,
  ...props
}: any) => <motion.button whileHover={{
  scale: 1.02
}} whileTap={{
  scale: 0.98
}} className={`
      px-6 md:px-8 py-3 md:py-4 rounded-full font-sans font-semibold text-white text-base md:text-lg shadow-lg
      transition-colors duration-300 flex items-center justify-center gap-2
      ${fullWidth ? 'w-full' : ''}
      ${className}
    `} style={{
  backgroundColor: COLORS.amber
}} {...props} data-id="element-11">
    {children}
  </motion.button>;
const Section = ({
  children,
  className = '',
  dark = false,
  ...props
}: {
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
  [key: string]: any;
}) => <section className={`py-6 md:py-12 px-4 md:px-6 lg:px-12 overflow-hidden ${className}`} style={{
  backgroundColor: dark ? COLORS.charcoal : COLORS.cream,
  color: dark ? COLORS.textDark : COLORS.textLight
}} {...props} data-id="element-12">
    <div className="max-w-4xl mx-auto" data-id="element-13">{children}</div>
  </section>;
export function LandingPage() {
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  return <div className="min-h-screen w-full overflow-x-hidden" data-id="element-14">
      {/* --- HERO SECTION --- */}
      <Section dark className="min-h-[85vh] md:min-h-[90vh] flex flex-col justify-center relative pt-20 md:pt-32 pb-16 md:pb-24" data-id="element-15">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="text-center space-y-6 md:space-y-8 z-10" data-id="element-16">
          <motion.div variants={itemVariant} className="flex justify-center mb-2 md:mb-4" data-id="element-17">
            <span className="px-3 md:px-4 py-1 md:py-1.5 rounded-full bg-slate-800 text-slate-300 text-xs md:text-sm font-medium tracking-wide border border-slate-700" data-id="element-18">
              GUÍA PDF DESCARGABLE
            </span>
          </motion.div>

          <motion.h1 variants={itemVariant} className="text-4xl md:text-7xl font-bold leading-tight text-white px-2" data-id="element-19">
            A las 3 AM <br data-id="element-20" />
            <span style={{
            color: COLORS.amber
          }} data-id="element-21">
              no se improvisa.
            </span>
          </motion.h1>

          <motion.p variants={itemVariant} className="text-lg md:text-2xl text-slate-300 max-w-2xl mx-auto leading-relaxed font-light px-2" data-id="element-22">
            Un sistema simple (sin gritos, sin guerra) para dejar de adivinar
            qué hacer en cada despertar.
          </motion.p>

          <motion.div variants={itemVariant} className="pt-6 md:pt-8 flex flex-col items-center gap-3 md:gap-4 px-2" data-id="element-23">
            <Button className="w-full md:w-auto md:min-w-[280px]" onClick={() => {
            document.getElementById('pricing')?.scrollIntoView({
              behavior: 'smooth'
            });
          }} data-id="element-24">
              Descarga el Mapa de Noches{' '}
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5" data-id="element-25" />
            </Button>
            <p className="text-xs md:text-sm text-slate-500" data-id="element-26">
              Descarga inmediata • Acceso de por vida
            </p>
          </motion.div>
        </motion.div>

        {/* Background decorative elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-20" data-id="element-27">
          <div className="absolute top-[-10%] right-[-5%] w-[300px] md:w-[500px] h-[300px] md:h-[500px] rounded-full blur-[80px] md:blur-[100px]" style={{
          background: COLORS.teal
        }} data-id="element-28"></div>
          <div className="absolute bottom-[-10%] left-[-10%] w-[250px] md:w-[400px] h-[250px] md:h-[400px] rounded-full blur-[60px] md:blur-[80px]" style={{
          background: COLORS.amber
        }} data-id="element-29"></div>
        </div>
      </Section>

      {/* --- AGITATION / PROBLEM --- */}
      <Section dark className="relative border-t border-slate-800" data-id="element-30">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeInUp} className="max-w-2xl mx-auto space-y-8 md:space-y-12" data-id="element-31">
          <div className="space-y-4 md:space-y-6 text-base md:text-xl leading-relaxed text-slate-300" data-id="element-32">
            <p className="font-serif text-2xl md:text-3xl text-white italic text-center" data-id="element-33">
              Son las 3:07 AM
            </p>
            <p data-id="element-34">Tu bebé llora. Tú te levantas como un resorte.</p>
            <p data-id="element-35">En tu cabeza empiezan las preguntas que nadie ve:</p>

            <ul className="space-y-3 md:space-y-4 pl-4 md:pl-6 border-l-2 border-slate-700 my-6 md:my-8 italic text-slate-400 text-sm md:text-base" data-id="element-36">
              <li data-id="element-37">"¿Tiene hambre o es costumbre?"</li>
              <li data-id="element-38">"¿Si lo cargo, lo acostumbro?"</li>
              <li data-id="element-39">"¿Por qué mi pareja no se mueve?"</li>
              <li data-id="element-40">"¿Por qué siento rabia… y luego culpa?"</li>
            </ul>

            <p data-id="element-41">La parte más dura no es el llanto.</p>
            <p className="font-semibold text-white" data-id="element-42">
              Es que cada despertar se siente como improvisar un incendio.
            </p>
          </div>
        </motion.div>
      </Section>

      {/* --- TRANSITION --- */}
      <div className="py-8 md:py-16 px-4 md:px-6 bg-gradient-to-b from-[#1a1c23] to-[#fdfbf7] text-center" data-id="element-43">
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="max-w-3xl mx-auto bg-white shadow-2xl rounded-xl md:rounded-2xl p-6 md:p-12 border border-stone-100" data-id="element-44">
          <h2 className="text-2xl md:text-4xl font-bold text-slate-900 mb-4 md:mb-6" data-id="element-45">
            No es falta de amor. <br data-id="element-46" />
            <span style={{
            color: COLORS.teal
          }} data-id="element-47">
              Es falta de sistema.
            </span>
          </h2>
          <p className="text-base md:text-lg text-slate-600 mb-6 md:mb-8" data-id="element-48">
            Hiciste rutinas que se rompen a los 2 días. Guardaste 20 videos de
            tips que no sirven a las 3am. El problema no es que tu bebé se
            despierte.{' '}
            <strong className="text-slate-900" data-id="element-49">
              El problema es que tú te despiertas sin mapa.
            </strong>
          </p>
          <div className="w-12 md:w-16 h-1 mx-auto rounded-full" style={{
          backgroundColor: COLORS.amber
        }} data-id="element-50"></div>
        </motion.div>
      </div>

      {/* --- SOLUTION REVEAL --- */}
      <Section data-id="element-51">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-52">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={staggerContainer} className="order-2 md:order-1" data-id="element-53">
            <motion.span variants={itemVariant} className="uppercase tracking-widest text-xs md:text-sm font-bold text-slate-400 mb-2 block" data-id="element-54">
              Presentamos
            </motion.span>
            <motion.h2 variants={itemVariant} className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 md:mb-6" data-id="element-55">
              Bebé Duerme, <br data-id="element-56" />
              <span style={{
              color: COLORS.teal
            }} data-id="element-57">
                Mamá Vive
              </span>
            </motion.h2>
            <motion.p variants={itemVariant} className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8 leading-relaxed" data-id="element-58">
              Un PDF práctico para mamás en la vida real. No promete magia. Te
              da algo mejor: <strong data-id="element-59">control</strong>.
            </motion.p>

            <motion.div variants={itemVariant} className="space-y-3 md:space-y-4 mb-6 md:mb-8" data-id="element-60">
              <div className="flex items-start gap-2 md:gap-3" data-id="element-61">
                <div className="mt-1 p-1 rounded-full bg-teal-100 text-teal-700 flex-shrink-0" data-id="element-62">
                  <Check size={14} strokeWidth={3} className="md:w-4 md:h-4" data-id="element-63" />
                </div>
                <p className="text-sm md:text-base text-slate-700" data-id="element-64">
                  <strong data-id="element-65">Diagnóstico en 60 segundos:</strong> Identifica qué
                  pasa realmente.
                </p>
              </div>
              <div className="flex items-start gap-2 md:gap-3" data-id="element-66">
                <div className="mt-1 p-1 rounded-full bg-teal-100 text-teal-700 flex-shrink-0" data-id="element-67">
                  <Check size={14} strokeWidth={3} className="md:w-4 md:h-4" data-id="element-68" />
                </div>
                <p className="text-sm md:text-base text-slate-700" data-id="element-69">
                  <strong data-id="element-70">Guiones de acción:</strong> Qué haces tú, qué hace tu
                  pareja.
                </p>
              </div>
              <div className="flex items-start gap-2 md:gap-3" data-id="element-71">
                <div className="mt-1 p-1 rounded-full bg-teal-100 text-teal-700 flex-shrink-0" data-id="element-72">
                  <Check size={14} strokeWidth={3} className="md:w-4 md:h-4" data-id="element-73" />
                </div>
                <p className="text-sm md:text-base text-slate-700" data-id="element-74">
                  <strong data-id="element-75">Plan de 7 noches:</strong> Estabiliza y ajusta sin
                  estrés.
                </p>
              </div>
            </motion.div>

            <motion.div variants={itemVariant} data-id="element-76">
              <Button className="w-full md:w-auto" onClick={() => {
              document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} data-id="element-77">
                Obtener el Mapa Ahora
              </Button>
            </motion.div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="order-1 md:order-2 relative" data-id="element-78">
            <div className="relative z-10 rounded-xl md:rounded-2xl overflow-hidden shadow-xl md:shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500" data-id="element-79">
              <img src="https://cdn.magicpatterns.com/uploads/uZd1tFk1hT343puHbYGtuU/Mockup_-_Bebe_duerme,_mama_vive.png" alt="Bebé Duerme Mamá Vive Guía en Tablet" className="w-full h-auto" data-id="element-80" />
            </div>
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-teal-50 rounded-full blur-2xl md:blur-3xl -z-10" data-id="element-81"></div>
          </motion.div>
        </div>
      </Section>

      {/* --- THE MECHANISM (4 SCENARIOS) --- */}
      <Section className="bg-stone-100" data-id="element-82">
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16" data-id="element-83">
          <h2 className="text-2xl md:text-4xl font-bold mb-3 md:mb-4 text-slate-900" data-id="element-84">
            El Mapa de Noches
          </h2>
          <p className="text-base md:text-lg text-slate-600" data-id="element-85">
            4 escenarios para que dejes de adivinar y empieces a decidir.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-id="element-86">
          {[{
          title: 'Hambre / Alimentación',
          icon: <Moon className="w-5 h-5 md:w-6 md:h-6" data-id="element-87" />,
          desc: 'Distingue hambre real de búsqueda de confort.'
        }, {
          title: 'Asociación de Sueño',
          icon: <Star className="w-5 h-5 md:w-6 md:h-6" data-id="element-88" />,
          desc: 'Cuando solo se duerme contigo o en brazos.'
        }, {
          title: 'Malestar Real',
          icon: <Shield className="w-5 h-5 md:w-6 md:h-6" data-id="element-89" />,
          desc: 'Protocolos para dientes, enfermedad o dolor.'
        }, {
          title: 'Caos por Cambios',
          icon: <Clock className="w-5 h-5 md:w-6 md:h-6" data-id="element-90" />,
          desc: 'Regresiones, viajes y cambios de rutina.'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white p-6 md:p-8 rounded-xl shadow-sm border border-stone-200" data-id="element-91">
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center mb-3 md:mb-4" data-id="element-92">
                {item.icon}
              </div>
              <h3 className="text-lg md:text-xl font-bold text-slate-900 mb-2" data-id="element-93">
                {item.title}
              </h3>
              <p className="text-sm md:text-base text-slate-600" data-id="element-94">{item.desc}</p>
            </motion.div>)}
        </div>
      </Section>

      {/* --- WHAT'S INCLUDED --- */}
      <Section data-id="element-95">
        <div className="bg-slate-900 rounded-2xl md:rounded-3xl p-6 md:p-16 text-white relative overflow-hidden" data-id="element-96">
          <div className="absolute top-0 right-0 w-48 h-48 md:w-64 md:h-64 bg-teal-900 rounded-full blur-[60px] md:blur-[80px] opacity-50" data-id="element-97"></div>

          <div className="relative z-10 grid md:grid-cols-2 gap-8 md:gap-12" data-id="element-98">
            <div data-id="element-99">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6" data-id="element-100">
                Qué incluye exactamente
              </h2>
              <p className="text-slate-300 text-base md:text-lg mb-6 md:mb-8" data-id="element-101">
                Todo lo que necesitas para tomar el control de tus noches, sin
                relleno y directo al grano.
              </p>
              <Button className="w-full md:w-auto" onClick={() => {
              document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} data-id="element-102">
                Sí, quiero el Mapa de Noches
              </Button>
            </div>

            <ul className="space-y-4 md:space-y-6" data-id="element-103">
              {['El Mapa de Noches (los 4 escenarios + diagnóstico)', 'Guiones listos para las 3am (para ti y tu pareja)', 'Protocolo de turnos y acuerdos claros', 'Plan de 7 noches paso a paso', 'Checklists imprimibles para la mesa de noche'].map((item, i) => <motion.li key={i} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-start gap-3 md:gap-4" data-id="element-104">
                  <div className="mt-0.5 md:mt-1 min-w-[20px] md:min-w-[24px] h-5 md:h-6 rounded-full border border-amber-500/50 flex items-center justify-center text-amber-500 flex-shrink-0" data-id="element-105">
                    <Check size={12} className="md:w-3.5 md:h-3.5" data-id="element-106" />
                  </div>
                  <span className="text-base md:text-lg text-slate-200" data-id="element-107">
                    {item}
                  </span>
                </motion.li>)}
            </ul>
          </div>
        </div>
      </Section>

      {/* --- AUDIENCE FILTER --- */}
      <Section data-id="element-108">
        <div className="grid md:grid-cols-2 gap-6 md:gap-12" data-id="element-109">
          <div className="bg-teal-50 p-6 md:p-8 rounded-xl md:rounded-2xl border border-teal-100" data-id="element-110">
            <h3 className="text-xl md:text-2xl font-bold text-teal-900 mb-4 md:mb-6 flex items-center gap-2" data-id="element-111">
              <Check className="text-teal-600 w-5 h-5 md:w-6 md:h-6" data-id="element-112" /> Es para
              ti si...
            </h3>
            <ul className="space-y-3 md:space-y-4" data-id="element-113">
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-114">
                <span className="font-bold" data-id="element-115">•</span> Quieres estructura sin
                dureza ni gritos.
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-116">
                <span className="font-bold" data-id="element-117">•</span> Estás cansada de "probar
                cosas" sin un plan claro.
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-118">
                <span className="font-bold" data-id="element-119">•</span> Quieres dormir más, pero
                también pelear menos con tu pareja.
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-teal-800" data-id="element-120">
                <span className="font-bold" data-id="element-121">•</span> Quieres dejar de sentir
                culpa por ser humana.
              </li>
            </ul>
          </div>

          <div className="bg-stone-50 p-6 md:p-8 rounded-xl md:rounded-2xl border border-stone-200" data-id="element-122">
            <h3 className="text-xl md:text-2xl font-bold text-slate-700 mb-4 md:mb-6 flex items-center gap-2" data-id="element-123">
              <X className="text-slate-400 w-5 h-5 md:w-6 md:h-6" data-id="element-124" /> No es para
              ti si...
            </h3>
            <ul className="space-y-3 md:space-y-4" data-id="element-125">
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-slate-600" data-id="element-126">
                <span className="font-bold" data-id="element-127">•</span> Buscas una promesa mágica
                tipo "12 horas garantizadas mañana".
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-slate-600" data-id="element-128">
                <span className="font-bold" data-id="element-129">•</span> Quieres un método único
                rígido que ignore las necesidades de tu bebé.
              </li>
              <li className="flex gap-2 md:gap-3 text-sm md:text-base text-slate-600" data-id="element-130">
                <span className="font-bold" data-id="element-131">•</span> No estás dispuesta a
                sostener consistencia por al menos 7 noches.
              </li>
            </ul>
          </div>
        </div>
      </Section>

      {/* --- PRICING & FINAL CTA --- */}
      <Section className="text-center pb-12 md:pb-20" id="pricing" data-id="element-132">
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-xl mx-auto" data-id="element-133">
          <div className="mb-6" data-id="element-134">
            <p className="text-slate-500 text-lg md:text-xl mb-2" data-id="element-135">
              <span className="line-through" data-id="element-136">$27.00 USD</span>
            </p>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-3 md:mb-4" data-id="element-137">
              Solo $6.39 USD
            </h2>
            <p className="text-teal-600 font-semibold text-sm md:text-base" data-id="element-138">
              ¡Ahorra más del 75%!
            </p>
          </div>
          <p className="text-lg md:text-xl text-slate-600 mb-6 md:mb-8" data-id="element-139">
            Un pago único. Lo descargas y lo usas esta noche.
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 md:p-6 mb-6 md:mb-8" data-id="element-140">
            <p className="text-sm md:text-base text-amber-900 font-medium" data-id="element-141">
              "Deja de negociar con el sueño roto. Recupera el control hoy
              mismo."
            </p>
          </div>

          <Button fullWidth className="text-lg md:text-xl py-5 md:py-6 shadow-xl hover:shadow-2xl transform transition-all" onClick={() => {
          window.location.href = 'https://instaflowdp.com/cart/63119376777585:1';
        }} data-id="element-142">
            Descargar "Bebé Duerme, Mamá Vive"
          </Button>

          <p className="mt-4 md:mt-6 text-xs md:text-sm text-slate-400" data-id="element-143">
            Pago seguro • Entrega digital inmediata al correo
          </p>
        </motion.div>
      </Section>

      {/* --- FOOTER --- */}
      <footer className="bg-slate-900 text-slate-500 py-8 md:py-10 text-center text-xs md:text-sm border-t border-slate-800" data-id="element-144">
        <div className="max-w-4xl mx-auto px-4 md:px-6" data-id="element-145">
          <p className="mb-4" data-id="element-146">
            &copy; {new Date().getFullYear()} Bebé Duerme, Mamá Vive. Todos los
            derechos reservados.
          </p>
          <p className="mb-4" data-id="element-147">
            Este producto no sustituye el consejo médico profesional. Siempre
            consulta con tu pediatra.
          </p>
          <div className="flex justify-center gap-6 text-slate-400" data-id="element-148">
            <button onClick={() => setModalContent('privacy')} className="hover:text-slate-300 transition-colors underline" data-id="element-149">
              Política de Privacidad
            </button>
            <button onClick={() => setModalContent('terms')} className="hover:text-slate-300 transition-colors underline" data-id="element-150">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* --- MODALS --- */}
      <Modal isOpen={modalContent === 'privacy'} onClose={() => setModalContent(null)} title="Política de Privacidad" data-id="element-151">
        <div className="prose prose-slate max-w-none text-sm md:text-base" data-id="element-152">
          <p className="text-slate-600 mb-4" data-id="element-153">
            <strong data-id="element-154">Última actualización:</strong>{' '}
            {new Date().toLocaleDateString('es-ES')}
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-155">
            1. Información que Recopilamos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-156">
            Al adquirir "Bebé Duerme, Mamá Vive", recopilamos únicamente la
            información necesaria para procesar tu compra y entregarte el
            producto digital:
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-157">
            <li data-id="element-158">Nombre y dirección de correo electrónico</li>
            <li data-id="element-159">
              Información de pago (procesada de forma segura por nuestro
              procesador de pagos)
            </li>
            <li data-id="element-160">
              Dirección IP y datos de navegación para prevención de fraude
            </li>
          </ul>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-161">
            2. Uso de la Información
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-162">
            Utilizamos tu información exclusivamente para:
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-163">
            <li data-id="element-164">Procesar tu compra y enviarte el PDF descargable</li>
            <li data-id="element-165">Enviarte el recibo de compra y confirmación de pedido</li>
            <li data-id="element-166">Proporcionar soporte técnico si lo solicitas</li>
            <li data-id="element-167">Cumplir con obligaciones legales y fiscales</li>
          </ul>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-168">
            3. Protección de Datos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-169">
            Implementamos medidas de seguridad técnicas y organizativas para
            proteger tu información personal contra acceso no autorizado,
            pérdida o alteración. Tus datos de pago son procesados de forma
            segura por procesadores certificados PCI-DSS.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-170">
            4. Compartir Información
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-171">
            No vendemos, alquilamos ni compartimos tu información personal con
            terceros, excepto:
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-172">
            <li data-id="element-173">
              Procesadores de pago necesarios para completar la transacción
            </li>
            <li data-id="element-174">Proveedores de servicios de entrega de correo electrónico</li>
            <li data-id="element-175">Cuando sea requerido por ley o autoridades competentes</li>
          </ul>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-176">
            5. Tus Derechos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-177">Tienes derecho a:</p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-178">
            <li data-id="element-179">Acceder a tu información personal</li>
            <li data-id="element-180">Solicitar corrección de datos inexactos</li>
            <li data-id="element-181">
              Solicitar eliminación de tus datos (sujeto a obligaciones legales)
            </li>
            <li data-id="element-182">Oponerte al procesamiento de tus datos</li>
            <li data-id="element-183">Retirar tu consentimiento en cualquier momento</li>
          </ul>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-184">
            6. Cookies
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-185">
            Utilizamos cookies esenciales para el funcionamiento del sitio y
            procesamiento de pagos. No utilizamos cookies de seguimiento o
            publicidad.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-186">
            7. Contacto
          </h3>
          <p className="text-slate-700" data-id="element-187">
            Para ejercer tus derechos o realizar consultas sobre privacidad,
            contáctanos a través del correo electrónico proporcionado en tu
            recibo de compra.
          </p>
        </div>
      </Modal>

      <Modal isOpen={modalContent === 'terms'} onClose={() => setModalContent(null)} title="Términos y Condiciones" data-id="element-188">
        <div className="prose prose-slate max-w-none text-sm md:text-base" data-id="element-189">
          <p className="text-slate-600 mb-4" data-id="element-190">
            <strong data-id="element-191">Última actualización:</strong>{' '}
            {new Date().toLocaleDateString('es-ES')}
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-192">
            1. Aceptación de Términos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-193">
            Al adquirir "Bebé Duerme, Mamá Vive", aceptas estos términos y
            condiciones en su totalidad. Si no estás de acuerdo, no debes
            realizar la compra.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-194">
            2. Descripción del Producto
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-195">
            "Bebé Duerme, Mamá Vive" es una guía digital en formato PDF que
            proporciona:
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-196">
            <li data-id="element-197">El Mapa de Noches con 4 escenarios y diagnóstico rápido</li>
            <li data-id="element-198">Guiones de acción para despertares nocturnos</li>
            <li data-id="element-199">Protocolo de turnos y acuerdos entre cuidadores</li>
            <li data-id="element-200">Plan de implementación de 7 noches</li>
            <li data-id="element-201">Checklists imprimibles</li>
          </ul>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-202">
            3. Precio y Pago
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-203">
            El precio del producto es de $6.39 USD. Este es un pago único que te
            otorga acceso permanente al PDF. Los pagos se procesan de forma
            segura a través de procesadores de pago certificados.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-204">
            4. Entrega del Producto
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-205">
            El PDF será enviado inmediatamente a tu correo electrónico tras
            confirmar el pago. Si no recibes el producto en 24 horas, contacta a
            soporte. Eres responsable de proporcionar una dirección de correo
            válida.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-206">
            5. Uso del Producto
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-207">
            El PDF es para tu uso personal únicamente. No está permitido:
          </p>
          <ul className="list-disc pl-6 text-slate-700 space-y-2 mb-4" data-id="element-208">
            <li data-id="element-209">Compartir, distribuir o revender el contenido</li>
            <li data-id="element-210">Publicar el contenido en sitios web o redes sociales</li>
            <li data-id="element-211">Modificar o crear obras derivadas sin autorización</li>
            <li data-id="element-212">Utilizar el contenido con fines comerciales</li>
          </ul>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-213">
            6. Propiedad Intelectual
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-214">
            Todo el contenido de "Bebé Duerme, Mamá Vive" está protegido por
            derechos de autor. La compra te otorga una licencia de uso personal,
            no la propiedad del contenido.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-215">
            7. Política de Reembolsos
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-216">
            Debido a la naturaleza digital del producto, no ofrecemos reembolsos
            una vez que el PDF ha sido descargado o enviado. Si experimentas
            problemas técnicos con la descarga, contacta a soporte para
            asistencia.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-217">
            8. Descargo de Responsabilidad Médica
          </h3>
          <p className="text-slate-700 mb-4 font-semibold" data-id="element-218">
            IMPORTANTE: Este producto NO sustituye el consejo médico
            profesional.
          </p>
          <p className="text-slate-700 mb-4" data-id="element-219">
            "Bebé Duerme, Mamá Vive" es una guía informativa y educativa. No
            proporciona diagnósticos médicos ni tratamientos. Siempre consulta
            con tu pediatra antes de implementar cambios en las rutinas de sueño
            de tu bebé, especialmente si tu bebé tiene condiciones médicas
            preexistentes.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-220">
            9. Limitación de Responsabilidad
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-221">
            No garantizamos resultados específicos. Los resultados pueden variar
            según cada familia y bebé. No nos hacemos responsables por daños
            directos, indirectos o consecuentes derivados del uso de este
            producto.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-222">
            10. Modificaciones
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-223">
            Nos reservamos el derecho de modificar estos términos en cualquier
            momento. Los cambios entrarán en vigor inmediatamente tras su
            publicación. Tu uso continuado del producto constituye aceptación de
            los términos modificados.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-224">
            11. Ley Aplicable
          </h3>
          <p className="text-slate-700 mb-4" data-id="element-225">
            Estos términos se rigen por las leyes aplicables en tu jurisdicción.
            Cualquier disputa será resuelta en los tribunales competentes de tu
            localidad.
          </p>

          <h3 className="text-lg font-bold text-slate-900 mt-6 mb-3" data-id="element-226">
            12. Contacto
          </h3>
          <p className="text-slate-700" data-id="element-227">
            Para preguntas sobre estos términos, contáctanos a través del correo
            electrónico proporcionado en tu recibo de compra.
          </p>
        </div>
      </Modal>
    </div>;
}